import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import {
  Users,
  Building,
  GraduationCap,
  Heart,
  User,
  Calendar,
  Clock,
  Filter,
  Accessibility,
  GraduationCap as GradCap,
  Crown,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";

import { ResponsiveHeatMap } from "@nivo/heatmap";
import { ResponsiveFunnel } from "@nivo/funnel";
import { Grid, Card } from "../../styles/GlobalStyles";
import { StatsCard } from "./StatsCard";
const chartsData = require("../../data/homepage-charts.json");

// Hook para detectar tamanho da tela
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: 1024,
  });

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width <= 640,
        isTablet: width > 640 && width <= 1024,
        isDesktop: width > 1024,
        width,
      });
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  return screenSize;
};

// Funções para obter configurações responsivas dos gráficos
const getResponsiveMargins = (
  type: "pie" | "bar" | "line" | "heatmap" | "funnel",
  screenSize: any
) => {
  const { isMobile, isTablet } = screenSize;

  const margins = {
    pie: {
      mobile: { top: 10, right: 20, bottom: 100, left: 20 },
      tablet: { top: 30, right: 80, bottom: 100, left: 80 },
      desktop: { top: 40, right: 120, bottom: 120, left: 120 },
    },
    bar: {
      mobile: { top: 10, right: 10, bottom: 100, left: 40 },
      tablet: { top: 30, right: 40, bottom: 100, left: 80 },
      desktop: { top: 40, right: 80, bottom: 140, left: 100 },
    },
    line: {
      mobile: { top: 10, right: 10, bottom: 45, left: 40 },
      tablet: { top: 25, right: 40, bottom: 80, left: 80 },
      desktop: { top: 30, right: 60, bottom: 90, left: 100 },
    },
    heatmap: {
      mobile: { top: 20, right: 30, bottom: 50, left: 30 },
      tablet: { top: 60, right: 100, bottom: 90, left: 100 },
      desktop: { top: 80, right: 140, bottom: 100, left: 140 },
    },
    funnel: {
      mobile: { top: 10, right: 10, bottom: 10, left: 10 },
      tablet: { top: 25, right: 25, bottom: 25, left: 25 },
      desktop: { top: 30, right: 30, bottom: 30, left: 120 },
    },
  };

  if (isMobile) return margins[type].mobile;
  if (isTablet) return margins[type].tablet;
  return margins[type].desktop;
};

const getResponsiveLegendConfig = (screenSize: any) => {
  const { isMobile, isTablet } = screenSize;

  if (isMobile) {
    return {
      anchor: "bottom" as const,
      direction: "column" as const,
      justify: false,
      translateX: 0,
      translateY: 50,
      itemsSpacing: 6,
      itemWidth: 200,
      itemHeight: 16,
      itemTextColor: "#64748b",
      itemDirection: "left-to-right" as const,
      itemOpacity: 1,
      symbolSize: 12,
      symbolShape: "circle" as const,
    };
  }

  if (isTablet) {
    return {
      anchor: "bottom" as const,
      direction: "row" as const,
      justify: true,
      translateX: 0,
      translateY: 60,
      itemsSpacing: 10,
      itemWidth: 100,
      itemHeight: 18,
      itemTextColor: "#64748b",
      itemDirection: "left-to-right" as const,
      itemOpacity: 1,
      symbolSize: 14,
      symbolShape: "circle" as const,
    };
  }

  return {
    anchor: "bottom" as const,
    direction: "row" as const,
    justify: true,
    translateX: 0,
    translateY: 80,
    itemsSpacing: 40,
    itemWidth: 150,
    itemHeight: 24,
    itemTextColor: "#64748b",
    itemDirection: "left-to-right" as const,
    itemOpacity: 1,
    symbolSize: 18,
    symbolShape: "circle" as const,
  };
};

const getResponsiveFontSizes = (screenSize: any) => {
  const { isMobile, isTablet } = screenSize;

  if (isMobile) {
    return {
      axis: 10,
      legend: 12,
      label: 10,
      tooltip: 12,
    };
  }

  if (isTablet) {
    return {
      axis: 12,
      legend: 14,
      label: 12,
      tooltip: 13,
    };
  }

  return {
    axis: 13,
    legend: 16,
    label: 14,
    tooltip: 14,
  };
};

// Função para obter tema responsivo dos gráficos
const getResponsiveChartTheme = (screenSize: any) => {
  const fontSizes = getResponsiveFontSizes(screenSize);

  return {
    background: "#FFFFFF",
    textColor: "#1E293B",
    fontSize: fontSizes.tooltip,
    axis: {
      domain: {
        line: {
          stroke: "#E2E8F0",
          strokeWidth: 1,
        },
      },
      legend: {
        text: {
          fontSize: fontSizes.legend,
          fontWeight: 600,
          fill: "#475569",
        },
      },
      ticks: {
        line: {
          stroke: "#E2E8F0",
          strokeWidth: 1,
        },
        text: {
          fontSize: fontSizes.axis,
          fill: "#64748B",
          fontWeight: 500,
        },
      },
    },
    grid: {
      line: {
        stroke: "#F1F5F9",
        strokeWidth: 1,
      },
    },
    legends: {
      title: {
        text: {
          fontSize: fontSizes.legend,
          fontWeight: 600,
          fill: "#475569",
        },
      },
      text: {
        fontSize: fontSizes.axis,
        fontWeight: 500,
        fill: "#64748B",
      },
    },
    labels: {
      text: {
        fontSize: fontSizes.label,
        fontWeight: 600,
        fill: "#FFFFFF",
      },
    },
    dots: {
      text: {
        fontSize: fontSizes.axis,
        fontWeight: 500,
        fill: "#1E293B",
      },
    },
    tooltip: {
      container: {
        background: "#FFFFFF",
        color: "#1E293B",
        fontSize: fontSizes.tooltip,
        borderRadius: "8px",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        border: "1px solid #E2E8F0",
      },
    },
  };
};

// Paleta de cores equilibrada - moderna e com presença
const professionalColors = [
  "#4A90E2", // Azul moderno
  "#5B9A3E", // Verde equilibrado
  "#E85C7A", // Rosa coral
  "#9C6ADE", // Roxo suave
  "#F5A623", // Laranja dourado
  "#50E3C2", // Turquesa
  "#E94B6B", // Rosa vibrante
  "#6B9A4A", // Verde lima suave
  "#6C7BE8", // Azul lavanda
  "#D4A574", // Caramelo
];

const HomeContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const ContentWrapper = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ChartCard = styled(Card)`
  height: 600px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 500px;
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 450px;
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const LargeChartCard = styled(ChartCard)`
  height: 700px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 600px;
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 550px;
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const CompanySection = styled.div`
  margin-bottom: 4rem;
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg}
    0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md}
      ${({ theme }) => theme.spacing.md} 0;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm}
      ${({ theme }) => theme.spacing.sm} 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 480px;
  padding: 1.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
    min-height: 400px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.5rem;
    min-height: 350px;
  }
`;

const FilterContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 16px;
    border-radius: 8px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const FilterTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: unset;
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: unset;
    width: 100%;
    padding: 10px 12px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px;
    font-size: 16px; /* Evita zoom em iOS */
  }
`;

const StatsGrid = styled(Grid)`
  margin-bottom: 4rem;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const IndicadoresGrid = styled(Grid)`
  margin-bottom: 4rem;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const IndicadoresPerfil: React.FC = () => {
  const screenSize = useResponsive();
  const [selectedEmpresa, setSelectedEmpresa] = useState<string>("Todos");
  const [selectedFilial, setSelectedFilial] = useState<string>("Todos");
  const [selectedDepartamento, setSelectedDepartamento] =
    useState<string>("Todos");

  // Função para calcular dados filtrados
  const filteredData = useMemo(() => {
    try {
      // Acessar os dados da "Empresa Demonstração"
      const empresaData = (chartsData as any)?.EMPRESA_DEMONSTRACAO;

      if (!empresaData) {
        console.error("Dados da empresa não encontrados");
        return null;
      }

      // Lógica de filtro baseada na seleção
      let currentData;

      if (selectedEmpresa === "Todos") {
        // Mostrar dados consolidados de todas as empresas
        currentData = empresaData.Todos?.Todos;
      } else {
        // Filtrar por empresa específica
        // Usar o nome da empresa diretamente como chave
        const empresaKey = selectedEmpresa;

        if (empresaKey && empresaData[empresaKey]) {
          currentData = empresaData[empresaKey].Todos;
        } else {
          console.log(`Empresa não encontrada: ${empresaKey}`);
          currentData = empresaData.Todos?.Todos;
        }
      }

      // Aplicar filtro de filial se selecionada
      if (selectedFilial !== "Todos" && currentData) {
        // Filtrar dados por filial específica
        const filialData = JSON.parse(JSON.stringify(currentData)); // Clone profundo

        // Filtrar filiaisData para mostrar apenas a filial selecionada
        if (filialData.filiaisData) {
          filialData.filiaisData = filialData.filiaisData.filter(
            (filial: any) => filial.label === selectedFilial
          );

          // Recalcular indicadores baseado na filial selecionada
          if (filialData.filiaisData.length > 0) {
            const filialValue = filialData.filiaisData[0].value;
            const proporcao =
              filialValue / (currentData.indicadores?.totalColaboradores || 1);

            filialData.indicadores = {
              totalColaboradores: filialValue,
              totalPcD: Math.round(
                (currentData.indicadores?.totalPcD || 0) * proporcao
              ),
              totalJovensAprendizes: Math.round(
                (currentData.indicadores?.totalJovensAprendizes || 0) *
                  proporcao
              ),
              totalLiderancas: Math.round(
                (currentData.indicadores?.totalLiderancas || 0) * proporcao
              ),
            };

            // Ajustar outros dados proporcionalmente
            if (filialData.familiaCargoData) {
              filialData.familiaCargoData = filialData.familiaCargoData.map(
                (item: any) => ({
                  ...item,
                  quantidade: Math.round(item.quantidade * proporcao),
                })
              );
            }

            if (filialData.estadoCivilData) {
              filialData.estadoCivilData = filialData.estadoCivilData.map(
                (item: any) => ({
                  ...item,
                  value: Math.round(item.value * proporcao),
                })
              );
            }

            if (filialData.generoData) {
              filialData.generoData = filialData.generoData.map(
                (item: any) => ({
                  ...item,
                  value: Math.round(item.value * proporcao),
                })
              );
            }

            if (filialData.idadeData) {
              filialData.idadeData = filialData.idadeData.map((item: any) => ({
                ...item,
                quantidade: Math.round(item.quantidade * proporcao),
              }));
            }

            if (filialData.escolaridadeData) {
              filialData.escolaridadeData = filialData.escolaridadeData.map(
                (item: any) => ({
                  ...item,
                  value: Math.round(item.value * proporcao),
                })
              );
            }

            if (filialData.tempoCasaData) {
              filialData.tempoCasaData = filialData.tempoCasaData.map(
                (serie: any) => ({
                  ...serie,
                  data: serie.data.map((point: any) => ({
                    ...point,
                    y: Math.round(point.y * proporcao),
                  })),
                })
              );
            }
          }
        }

        currentData = filialData;
      }

      // Aplicar filtro de departamento se selecionado
      if (selectedDepartamento !== "Todos" && currentData) {
        // Simular dados por departamento baseado nos dados principais
        const deptData = JSON.parse(JSON.stringify(currentData)); // Clone profundo

        // Fatores de distribuição por departamento (simulados)
        const deptFactors: { [key: string]: number } = {
          Tecnologia: 0.35,
          "Recursos Humanos": 0.15,
          Financeiro: 0.12,
          Comercial: 0.2,
          Operações: 0.12,
          Marketing: 0.06,
        };

        const factor = deptFactors[selectedDepartamento] || 0.1;

        // Ajustar indicadores
        if (deptData.indicadores) {
          deptData.indicadores = {
            totalColaboradores: Math.round(
              deptData.indicadores.totalColaboradores * factor
            ),
            totalPcD: Math.round(deptData.indicadores.totalPcD * factor),
            totalJovensAprendizes: Math.round(
              deptData.indicadores.totalJovensAprendizes * factor
            ),
            totalLiderancas: Math.round(
              deptData.indicadores.totalLiderancas * factor
            ),
          };
        }

        // Ajustar outros dados proporcionalmente
        if (deptData.familiaCargoData) {
          deptData.familiaCargoData = deptData.familiaCargoData.map(
            (item: any) => ({
              ...item,
              quantidade: Math.round(item.quantidade * factor),
            })
          );
        }

        if (deptData.estadoCivilData) {
          deptData.estadoCivilData = deptData.estadoCivilData.map(
            (item: any) => ({
              ...item,
              value: Math.round(item.value * factor),
            })
          );
        }

        if (deptData.generoData) {
          deptData.generoData = deptData.generoData.map((item: any) => ({
            ...item,
            value: Math.round(item.value * factor),
          }));
        }

        if (deptData.idadeData) {
          deptData.idadeData = deptData.idadeData.map((item: any) => ({
            ...item,
            quantidade: Math.round(item.quantidade * factor),
          }));
        }

        if (deptData.escolaridadeData) {
          deptData.escolaridadeData = deptData.escolaridadeData.map(
            (item: any) => ({
              ...item,
              value: Math.round(item.value * factor),
            })
          );
        }

        if (deptData.tempoCasaData) {
          deptData.tempoCasaData = deptData.tempoCasaData.map((serie: any) => ({
            ...serie,
            data: serie.data.map((point: any) => ({
              ...point,
              y: Math.round(point.y * factor),
            })),
          }));
        }

        // Para departamento, não mostrar filiaisData separadas
        if (deptData.filiaisData) {
          deptData.filiaisData = [
            {
              id: `DEPT_${selectedDepartamento.toUpperCase()}`,
              label: `Departamento ${selectedDepartamento}`,
              empresa: selectedEmpresa,
              value: deptData.indicadores?.totalColaboradores || 0,
              color: "#6B7280",
            },
          ];
        }

        currentData = deptData;
      }

      return currentData;
    } catch (error) {
      console.error("Erro ao processar dados:", error);
      return null;
    }
  }, [selectedEmpresa, selectedFilial, selectedDepartamento]);

  const getEmpresas = () => {
    try {
      const empresaData = (chartsData as any)?.EMPRESA_DEMONSTRACAO;
      if (!empresaData) return ["Todos"];

      const empresas = ["Todos"];
      Object.keys(empresaData).forEach((key) => {
        if (key !== "Todos" && empresaData[key]?.Todos?.empresaData?.[0]) {
          empresas.push(empresaData[key].Todos.empresaData[0].label);
        }
      });
      return empresas;
    } catch (error) {
      return ["Todos"];
    }
  };

  const getFiliais = () => {
    try {
      const empresaData = (chartsData as any)?.EMPRESA_DEMONSTRACAO;
      if (!empresaData) return ["Todos"];

      const filiais = ["Todos"];

      if (selectedEmpresa === "Todos") {
        // Mostrar todas as filiais de todas as empresas
        empresaData.Todos?.Todos?.filiaisData?.forEach((filial: any) => {
          if (filial.label && !filiais.includes(filial.label)) {
            filiais.push(filial.label);
          }
        });
      } else {
        // Mostrar filiais da empresa selecionada
        const empresaKey = Object.keys(empresaData).find(
          (key) =>
            empresaData[key]?.Todos?.empresaData?.[0]?.label === selectedEmpresa
        );

        if (empresaKey && empresaData[empresaKey]?.Todos?.filiaisData) {
          empresaData[empresaKey].Todos.filiaisData.forEach((filial: any) => {
            if (filial.label && !filiais.includes(filial.label)) {
              filiais.push(filial.label);
            }
          });
        }
      }

      return filiais;
    } catch (error) {
      return ["Todos"];
    }
  };

  const getDepartamentos = () => {
    return [
      "Todos",
      "Tecnologia",
      "Recursos Humanos",
      "Financeiro",
      "Comercial",
      "Operações",
      "Marketing",
    ];
  };

  return (
    <HomeContainer>
      <ContentWrapper>
        <PageHeader>
          <PageTitle>Indicadores de perfil da população</PageTitle>
        </PageHeader>

        {/* Filtros */}
        <FilterContainer>
          <FilterHeader>
            <Filter size={24} color="#60A5FA" />
            <FilterTitle>Filtros Avançados</FilterTitle>
          </FilterHeader>
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>Empresa</FilterLabel>
              <FilterSelect
                value={selectedEmpresa}
                onChange={(e) => {
                  setSelectedEmpresa(e.target.value);
                  setSelectedFilial("Todos");
                  setSelectedDepartamento("Todos");
                }}
              >
                {getEmpresas().map((empresa) => (
                  <option key={`empresa-${empresa}`} value={empresa}>
                    {empresa === "Todos" ? "Todas as Empresas" : empresa}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Filial</FilterLabel>
              <FilterSelect
                value={selectedFilial}
                onChange={(e) => {
                  setSelectedFilial(e.target.value);
                  setSelectedDepartamento("Todos");
                }}
              >
                {getFiliais().map((filial) => (
                  <option key={`filial-${filial}`} value={filial}>
                    {filial}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Departamento</FilterLabel>
              <FilterSelect
                value={selectedDepartamento}
                onChange={(e) => setSelectedDepartamento(e.target.value)}
              >
                {getDepartamentos().map((dept) => (
                  <option key={`dept-${dept}`} value={dept}>
                    {dept}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>
          </FilterGrid>
        </FilterContainer>

        {/* Indicadores Principais */}
        <IndicadoresGrid columns={4} minColumnWidth="280px">
          <StatsCard
            title="Total de Colaboradores"
            value={filteredData.indicadores?.totalColaboradores || 0}
            icon={<Users size={28} />}
            color="#1E40AF"
            bgColor="#1E40AF"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Colaboradores PcD"
            value={filteredData.indicadores?.totalPcD || 0}
            icon={<Accessibility size={28} />}
            color="#059669"
            bgColor="#059669"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Jovens Aprendizes"
            value={filteredData.indicadores?.totalJovensAprendizes || 0}
            icon={<GradCap size={28} />}
            color="#DC2626"
            bgColor="#DC2626"
            iconColor="#FFFFFF"
          />
          <StatsCard
            title="Lideranças Executivas"
            value={filteredData.indicadores?.totalLiderancas || 0}
            icon={<Crown size={28} />}
            color="#7C3AED"
            bgColor="#7C3AED"
            iconColor="#FFFFFF"
          />
        </IndicadoresGrid>

        {/* Seção Separada para Distribuição Corporativa */}
        <CompanySection>
          {/* Gráfico de Empresas - Linha separada */}
          <LargeChartCard style={{ marginBottom: "2rem" }}>
            <ChartTitle>
              <Building size={24} />
              Distribuição por Empresas (Total:{" "}
              {filteredData?.indicadores?.totalColaboradores || 10422}{" "}
              colaboradores)
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={filteredData?.empresaData || []}
                margin={getResponsiveMargins("pie", screenSize)}
                innerRadius={screenSize.isMobile ? 0.3 : 0.45}
                padAngle={screenSize.isMobile ? 2 : 4}
                cornerRadius={screenSize.isMobile ? 6 : 10}
                activeOuterRadiusOffset={screenSize.isMobile ? 8 : 15}
                colors={{ datum: "data.color" }}
                borderWidth={screenSize.isMobile ? 2 : 4}
                borderColor="#ffffff"
                arcLinkLabelsSkipAngle={screenSize.isMobile ? 360 : 8}
                arcLinkLabelsTextColor="#64748b"
                arcLinkLabelsThickness={screenSize.isMobile ? 2 : 3}
                arcLinkLabelsColor={{ from: "color" }}
                arcLabelsSkipAngle={screenSize.isMobile ? 15 : 8}
                arcLabelsTextColor="#ffffff"
                arcLabelsRadiusOffset={0.6}
                valueFormat={(value) => {
                  const total = (filteredData?.empresaData || []).reduce(
                    (sum: number, item: any) => sum + item.value,
                    0
                  );
                  const percentage = total > 0 ? (value / total) * 100 : 0;
                  return `${percentage.toFixed(1)}%`;
                }}
                arcLinkLabelsTextOffset={5}
                arcLinkLabel={(d) => {
                  const total = (filteredData?.empresaData || []).reduce(
                    (sum: number, item: any) => sum + item.value,
                    0
                  );
                  const percentage = total > 0 ? (d.value / total) * 100 : 0;
                  return screenSize.isMobile
                    ? `${percentage.toFixed(1)}%`
                    : `${d.label} (${percentage.toFixed(1)}%)`;
                }}
                theme={{
                  ...getResponsiveChartTheme(screenSize),
                  background: "transparent",
                }}
                animate={true}
                motionConfig="gentle"
                legends={[
                  {
                    ...getResponsiveLegendConfig(screenSize),
                    translateY: screenSize.isMobile ? 65 : 80,
                  },
                ]}
              />
            </ChartContainer>
          </LargeChartCard>

          {/* Gráfico de Filiais - Linha separada */}
          <LargeChartCard style={{ height: "700px" }}>
            <ChartTitle>
              <Building size={24} />
              Distribuição por Unidades
            </ChartTitle>
            <ChartContainer>
              <ResponsiveBar
                data={(filteredData?.filiaisData || []).map((item: any) => ({
                  unidade: screenSize.isMobile
                    ? item.label
                        .replace("Matriz ", "M.")
                        .replace("Filial ", "F.")
                    : item.label
                        .replace("Matriz ", "Matriz\n")
                        .replace("Filial ", "Filial\n"),
                  colaboradores: item.value,
                  color: item.color,
                }))}
                keys={["colaboradores"]}
                indexBy="unidade"
                margin={getResponsiveMargins("bar", screenSize)}
                padding={screenSize.isMobile ? 0.2 : 0.15}
                valueScale={{ type: "linear" }}
                indexScale={{ type: "band", round: true }}
                colors={{ datum: "data.color" }}
                borderRadius={screenSize.isMobile ? 4 : 8}
                borderWidth={screenSize.isMobile ? 1 : 2}
                borderColor="#ffffff"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: screenSize.isMobile ? 15 : 15,
                  tickRotation: screenSize.isMobile ? -90 : -45,
                  legend: screenSize.isMobile ? "" : "",
                  legendPosition: "middle",
                  legendOffset: screenSize.isMobile ? 80 : 80,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 8,
                  tickRotation: 0,
                  legend: screenSize.isMobile ? "" : "Colaboradores",
                  legendPosition: "middle",
                  legendOffset: screenSize.isMobile ? -50 : -70,
                }}
                labelSkipWidth={screenSize.isMobile ? 50 : 12}
                labelSkipHeight={screenSize.isMobile ? 30 : 12}
                labelTextColor="#ffffff"
                theme={{
                  ...getResponsiveChartTheme(screenSize),
                  background: "transparent",
                }}
                animate={true}
                motionConfig="gentle"
              />
            </ChartContainer>
          </LargeChartCard>
        </CompanySection>

        {/* Seção Principal de Analytics */}

        {/* Família de Cargo - Gráfico de Funil - Linha separada */}
        {/* Família de Cargo - Gráfico de Funil */}
        <LargeChartCard style={{ marginBottom: "3rem" }}>
          <ChartTitle>
            <TrendingUp size={24} />
            Família de Cargo
          </ChartTitle>
          <ChartContainer style={{ position: "relative" }}>
            <ResponsiveFunnel
              data={(filteredData?.familiaCargoData || [])
                .sort((a: any, b: any) => b.quantidade - a.quantidade)
                .map((item: any) => ({
                  id: item.cargo,
                  value: item.quantidade,
                  label:
                    screenSize.isMobile && item.cargo.length > 15
                      ? item.cargo.substring(0, 12) + "..."
                      : item.cargo,
                }))}
              margin={getResponsiveMargins("funnel", screenSize)}
              valueFormat=">-.0f"
              colors={professionalColors}
              borderWidth={screenSize.isMobile ? 2 : 3}
              enableLabel={true}
              labelColor="#FFFFFF"
              beforeSeparatorLength={screenSize.isMobile ? 60 : 120}
              beforeSeparatorOffset={screenSize.isMobile ? 15 : 25}
              afterSeparatorLength={screenSize.isMobile ? 60 : 120}
              afterSeparatorOffset={screenSize.isMobile ? 15 : 25}
              currentPartSizeExtension={screenSize.isMobile ? 8 : 15}
              currentBorderWidth={screenSize.isMobile ? 30 : 50}
              motionConfig="gentle"
              theme={{
                ...getResponsiveChartTheme(screenSize),
                background: "transparent",
              }}
            />

            {/* Labels personalizadas ao lado do funnel - apenas desktop */}
            {!screenSize.isMobile &&
              (filteredData?.familiaCargoData || [])
                .sort((a: any, b: any) => b.quantidade - a.quantidade)
                .map((item: any, index: number) => {
                  const totalItems = (filteredData?.familiaCargoData || [])
                    .length;
                  const topPosition =
                    20 + (index * (100 - 40)) / (totalItems - 1);

                  return (
                    <div
                      key={item.cargo}
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: `${topPosition}%`,
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#666",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        zIndex: 10,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.cargo}
                    </div>
                  );
                })}
          </ChartContainer>
        </LargeChartCard>

        {/* Estado Civil - Gráfico de Barras Horizontais */}
        <ChartCard style={{ marginBottom: "3rem" }}>
          <ChartTitle>
            <Heart size={20} />
            Estado Civil
          </ChartTitle>
          <ChartContainer>
            <ResponsiveBar
              data={(filteredData?.estadoCivilData || []).map((item: any) => ({
                estado: item.label,
                valor: item.value,
                color: item.color,
              }))}
              keys={["valor"]}
              indexBy="estado"
              layout="vertical"
              margin={getResponsiveMargins("bar", screenSize)}
              padding={screenSize.isMobile ? 0.2 : 0.15}
              valueScale={{ type: "linear" }}
              indexScale={{ type: "band", round: true }}
              colors={professionalColors}
              borderRadius={screenSize.isMobile ? 3 : 4}
              borderWidth={1}
              borderColor="#FFFFFF"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: screenSize.isMobile ? "" : "Colaboradores",
                legendPosition: "middle",
                legendOffset: screenSize.isMobile ? 35 : 45,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "",
                legendPosition: "middle",
                legendOffset: screenSize.isMobile ? -35 : -40,
              }}
              labelSkipWidth={screenSize.isMobile ? 50 : 12}
              labelSkipHeight={screenSize.isMobile ? 30 : 12}
              labelTextColor="#FFFFFF"
              theme={getResponsiveChartTheme(screenSize)}
              animate={true}
              motionConfig="gentle"
            />
          </ChartContainer>
        </ChartCard>

        {/* Segunda linha: Diversidade de Gênero e Distribuição Etária */}
        <StatsGrid
          columns={2}
          minColumnWidth="600px"
          style={{ marginBottom: "3rem" }}
        >
          {/* Gênero - Gráfico de Pizza para Diversidade */}
          <ChartCard>
            <ChartTitle>
              <User size={20} />
              Gênero
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={filteredData?.generoData || []}
                margin={getResponsiveMargins("pie", screenSize)}
                innerRadius={screenSize.isMobile ? 0.3 : 0.4}
                padAngle={screenSize.isMobile ? 1 : 2}
                cornerRadius={screenSize.isMobile ? 2 : 4}
                activeOuterRadiusOffset={screenSize.isMobile ? 6 : 10}
                colors={{ datum: "data.color" }}
                borderWidth={screenSize.isMobile ? 1 : 2}
                borderColor="#ffffff"
                arcLinkLabelsSkipAngle={screenSize.isMobile ? 360 : 10}
                arcLinkLabelsTextColor="#64748b"
                arcLinkLabelsThickness={screenSize.isMobile ? 1 : 2}
                arcLinkLabelsColor={{ from: "color" }}
                arcLabelsSkipAngle={screenSize.isMobile ? 15 : 10}
                arcLabelsTextColor="#ffffff"
                arcLabelsRadiusOffset={0.6}
                valueFormat={(value) => {
                  const total = (filteredData?.generoData || []).reduce(
                    (sum: number, item: any) => sum + item.value,
                    0
                  );
                  const percentage = total > 0 ? (value / total) * 100 : 0;
                  return `${percentage.toFixed(1)}%`;
                }}
                theme={{
                  ...getResponsiveChartTheme(screenSize),
                  background: "transparent",
                }}
                animate={true}
                motionConfig="gentle"
                legends={[
                  {
                    ...getResponsiveLegendConfig(screenSize),
                    translateY: screenSize.isMobile ? 65 : 56,
                  },
                ]}
              />
            </ChartContainer>
          </ChartCard>

          {/* Faixa Etária - Gráfico de Barras Moderno com Gradiente */}
          <ChartCard>
            <ChartTitle>
              <Calendar size={20} />
              Idade
            </ChartTitle>
            <ChartContainer>
              <ResponsiveBar
                data={(filteredData?.idadeData || []).map((item: any) => ({
                  faixa: item.faixa,
                  quantidade: item.quantidade,
                  color: item.color,
                }))}
                keys={["quantidade"]}
                indexBy="faixa"
                margin={{ top: 20, right: 60, bottom: 80, left: 100 }}
                padding={0.2}
                valueScale={{ type: "linear" }}
                indexScale={{ type: "band", round: true }}
                colors={{ datum: "data.color" }}
                borderRadius={6}
                borderWidth={2}
                borderColor="#FFFFFF"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -30,
                  legend: "Faixa Etária",
                  legendPosition: "middle",
                  legendOffset: 70,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Colaboradores",
                  legendPosition: "middle",
                  legendOffset: -80,
                }}
                labelSkipWidth={screenSize.isMobile ? 50 : 12}
                labelSkipHeight={screenSize.isMobile ? 30 : 12}
                labelTextColor="#FFFFFF"
                theme={getResponsiveChartTheme(screenSize)}
                animate={true}
                motionConfig="gentle"
                role="application"
                ariaLabel="Gráfico de distribuição etária"
              />
            </ChartContainer>
          </ChartCard>
        </StatsGrid>

        {/* Terceira linha: Tempo de Permanência - Linha separada */}
        <LargeChartCard style={{ marginBottom: "3rem" }}>
          <ChartTitle>
            <Clock size={24} />
            Tempo de Casa
          </ChartTitle>
          <ChartContainer>
            <ResponsiveLine
              data={filteredData?.tempoCasaData || []}
              margin={getResponsiveMargins("line", screenSize)}
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                min: 0,
                max: "auto",
                stacked: false,
                reverse: false,
              }}
              yFormat=" >-.0f"
              curve="catmullRom"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: screenSize.isMobile ? -45 : -30,
                legend: screenSize.isMobile ? "" : "Tempo na Empresa",
                legendOffset: screenSize.isMobile ? 50 : 70,
                legendPosition: "middle",
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: screenSize.isMobile ? "" : "Colaboradores",
                legendOffset: screenSize.isMobile ? -50 : -80,
                legendPosition: "middle",
              }}
              enableGridX={false}
              enableGridY={true}
              pointSize={screenSize.isMobile ? 6 : 10}
              pointColor="#FFFFFF"
              pointBorderWidth={screenSize.isMobile ? 2 : 3}
              pointBorderColor={{ from: "serieColor" }}
              pointLabelYOffset={-12}
              enableArea={true}
              areaOpacity={0.15}
              useMesh={true}
              colors={["#1E40AF"]}
              lineWidth={screenSize.isMobile ? 3 : 4}
              animate={true}
              motionConfig="gentle"
              theme={getResponsiveChartTheme(screenSize)}
              enableSlices="x"
            />
          </ChartContainer>
        </LargeChartCard>

        {/* Quarta linha: Perfil Educacional - Linha separada */}
        <LargeChartCard style={{ marginBottom: "3rem" }}>
          <ChartTitle>
            <GraduationCap size={24} />
            Perfil Educacional
          </ChartTitle>
          <ChartContainer>
            <ResponsivePie
              data={filteredData?.escolaridadeData || []}
              margin={getResponsiveMargins("pie", screenSize)}
              innerRadius={screenSize.isMobile ? 0.5 : 0.65}
              padAngle={screenSize.isMobile ? 1 : 2}
              cornerRadius={screenSize.isMobile ? 4 : 6}
              activeOuterRadiusOffset={screenSize.isMobile ? 8 : 15}
              colors={professionalColors}
              borderWidth={screenSize.isMobile ? 2 : 4}
              borderColor="#e2e8f0"
              arcLinkLabelsSkipAngle={screenSize.isMobile ? 360 : 10}
              arcLinkLabelsTextColor="#64748b"
              arcLinkLabelsThickness={screenSize.isMobile ? 2 : 3}
              arcLinkLabelsColor={{ from: "color" }}
              arcLabelsSkipAngle={screenSize.isMobile ? 15 : 10}
              arcLabelsTextColor="#FFFFFF"
              arcLabelsRadiusOffset={0.6}
              valueFormat={(value) => {
                const total = (filteredData?.escolaridadeData || []).reduce(
                  (sum: number, item: any) => sum + item.value,
                  0
                );
                const percentage = total > 0 ? (value / total) * 100 : 0;
                return `${percentage.toFixed(1)}%`;
              }}
              theme={{
                ...getResponsiveChartTheme(screenSize),
                background: "transparent",
              }}
              animate={true}
              motionConfig="gentle"
              transitionMode="startAngle"
              legends={[
                {
                  ...getResponsiveLegendConfig(screenSize),
                  translateY: screenSize.isMobile ? 65 : 60,
                },
              ]}
            />
          </ChartContainer>
        </LargeChartCard>

        {/* HeatMap de Correlações - Seção Completa */}

        <LargeChartCard style={{ height: "500px" }}>
          <ChartTitle>
            <BarChart3 size={24} />
            Mapa de Calor: Idade vs Escolaridade
          </ChartTitle>
          <ChartContainer style={{ minHeight: "380px" }}>
            <ResponsiveHeatMap
              data={[
                {
                  id: "18-25",
                  data: [
                    { x: screenSize.isMobile ? "Sup" : "Superior", y: 890 },
                    { x: screenSize.isMobile ? "Pós" : "Pós-Grad", y: 234 },
                    { x: screenSize.isMobile ? "Téc" : "Técnico", y: 312 },
                    { x: "Médio", y: 127 },
                  ],
                },
                {
                  id: "26-35",
                  data: [
                    { x: screenSize.isMobile ? "Sup" : "Superior", y: 2190 },
                    { x: screenSize.isMobile ? "Pós" : "Pós-Grad", y: 891 },
                    { x: screenSize.isMobile ? "Téc" : "Técnico", y: 445 },
                    { x: "Médio", y: 121 },
                  ],
                },
                {
                  id: "36-45",
                  data: [
                    { x: screenSize.isMobile ? "Sup" : "Superior", y: 1875 },
                    { x: screenSize.isMobile ? "Pós" : "Pós-Grad", y: 937 },
                    { x: screenSize.isMobile ? "Téc" : "Técnico", y: 250 },
                    { x: "Médio", y: 63 },
                  ],
                },
                {
                  id: "46-55",
                  data: [
                    { x: screenSize.isMobile ? "Sup" : "Superior", y: 937 },
                    { x: screenSize.isMobile ? "Pós" : "Pós-Grad", y: 468 },
                    { x: screenSize.isMobile ? "Téc" : "Técnico", y: 125 },
                    { x: "Médio", y: 32 },
                  ],
                },
                {
                  id: "56+",
                  data: [
                    { x: screenSize.isMobile ? "Sup" : "Superior", y: 361 },
                    { x: screenSize.isMobile ? "Pós" : "Pós-Grad", y: 78 },
                    { x: screenSize.isMobile ? "Téc" : "Técnico", y: 68 },
                    { x: "Médio", y: 18 },
                  ],
                },
              ]}
              margin={getResponsiveMargins("heatmap", screenSize)}
              valueFormat=">-.0f"
              axisTop={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: screenSize.isMobile ? -45 : 0,
                legend: "",
                legendOffset: screenSize.isMobile ? 35 : 46,
              }}
              axisRight={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: screenSize.isMobile ? "" : "Faixa Etária",
                legendPosition: "middle",
                legendOffset: screenSize.isMobile ? 60 : 100,
              }}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: screenSize.isMobile ? -45 : 0,
                legend: screenSize.isMobile ? "" : "Nível de Escolaridade",
                legendPosition: "middle",
                legendOffset: screenSize.isMobile ? 60 : 80,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: screenSize.isMobile ? "" : "Faixa Etária",
                legendPosition: "middle",
                legendOffset: screenSize.isMobile ? -50 : -110,
              }}
              colors={{
                type: "sequential",
                scheme: "blues",
                minValue: 0,
                maxValue: 2500,
              }}
              emptyColor="rgba(248, 250, 252, 0.8)"
              borderColor="#e2e8f0"
              borderWidth={screenSize.isMobile ? 1 : 2}
              labelTextColor={(cell) => {
                // Usar texto escuro para valores baixos/cores claras e texto claro para valores altos/cores escuras
                return cell.value && cell.value > 1200 ? "#FFFFFF" : "#1E293B";
              }}
              animate={true}
              motionConfig="gentle"
              hoverTarget="cell"
              theme={{
                ...getResponsiveChartTheme(screenSize),
                background: "transparent",
              }}
            />
          </ChartContainer>
        </LargeChartCard>
      </ContentWrapper>
    </HomeContainer>
  );
};
